import { supabase } from "@/integrations/supabase/client";

export type AuditAction =
    | "CREATE"
    | "UPDATE"
    | "DELETE"
    | "VIEW"
    | "LOGIN"
    | "LOGOUT"
    | "UPLOAD"
    | "REORDER";

export type EntityType =
    | "service"
    | "portfolio_album"
    | "portfolio_media"
    | "booking"
    | "review"
    | "profile"
    | "category"
    | "social_link"
    | "footer_settings"
    | "hero_banner"
    | "admin_user";

interface AuditLogParams {
    action: AuditAction;
    entityType: EntityType;
    entityId?: string;
    details?: Record<string, any>;
}

/**
 * Creates an audit log entry for admin actions
 * @param params - The audit log parameters
 * @returns Promise<void>
 */
export async function createAuditLog({
    action,
    entityType,
    entityId,
    details
}: AuditLogParams): Promise<void> {
    try {
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();

        if (!user?.email) {
            console.warn('No user email found for audit log');
            return;
        }

        // Insert audit log
        const { error } = await supabase
            .from('admin_audit_log')
            .insert({
                admin_email: user.email,
                action,
                entity_type: entityType,
                entity_id: entityId || null,
                details: details || null
            });

        if (error) {
            console.error('Failed to create audit log:', error);
        }
    } catch (error) {
        console.error('Error creating audit log:', error);
    }
}

/**
 * Helper function to log service actions
 */
export const auditLog = {
    service: {
        create: (id: string, name: any) =>
            createAuditLog({ action: "CREATE", entityType: "service", entityId: id, details: { name } }),
        update: (id: string, name: any) =>
            createAuditLog({ action: "UPDATE", entityType: "service", entityId: id, details: { name } }),
        delete: (id: string) =>
            createAuditLog({ action: "DELETE", entityType: "service", entityId: id }),
        reorder: (id: string) =>
            createAuditLog({ action: "REORDER", entityType: "service", entityId: id }),
    },
    portfolio: {
        createAlbum: (id: string, title: any) =>
            createAuditLog({ action: "CREATE", entityType: "portfolio_album", entityId: id, details: { title } }),
        updateAlbum: (id: string, title: any) =>
            createAuditLog({ action: "UPDATE", entityType: "portfolio_album", entityId: id, details: { title } }),
        deleteAlbum: (id: string) =>
            createAuditLog({ action: "DELETE", entityType: "portfolio_album", entityId: id }),
        uploadMedia: (albumId: string, count: number) =>
            createAuditLog({ action: "UPLOAD", entityType: "portfolio_media", entityId: albumId, details: { count } }),
        deleteMedia: (mediaId: string) =>
            createAuditLog({ action: "DELETE", entityType: "portfolio_media", entityId: mediaId }),
        reorderMedia: (albumId: string) =>
            createAuditLog({ action: "REORDER", entityType: "portfolio_media", entityId: albumId }),
    },
    booking: {
        update: (id: string, status: string) =>
            createAuditLog({ action: "UPDATE", entityType: "booking", entityId: id, details: { status } }),
        delete: (id: string) =>
            createAuditLog({ action: "DELETE", entityType: "booking", entityId: id }),
    },
    review: {
        update: (id: string, approved: boolean) =>
            createAuditLog({ action: "UPDATE", entityType: "review", entityId: id, details: { approved } }),
        delete: (id: string) =>
            createAuditLog({ action: "DELETE", entityType: "review", entityId: id }),
    },
    profile: {
        update: () =>
            createAuditLog({ action: "UPDATE", entityType: "profile" }),
        uploadLogo: () =>
            createAuditLog({ action: "UPLOAD", entityType: "profile", details: { type: "logo" } }),
    },
    category: {
        create: (id: string, name: string) =>
            createAuditLog({ action: "CREATE", entityType: "category", entityId: id, details: { name } }),
        update: (id: string, name: string) =>
            createAuditLog({ action: "UPDATE", entityType: "category", entityId: id, details: { name } }),
        delete: (id: string) =>
            createAuditLog({ action: "DELETE", entityType: "category", entityId: id }),
    },
    socialLink: {
        update: () =>
            createAuditLog({ action: "UPDATE", entityType: "social_link" }),
    },
    footer: {
        update: () =>
            createAuditLog({ action: "UPDATE", entityType: "footer_settings" }),
    },
    heroBanner: {
        create: (id: string) =>
            createAuditLog({ action: "CREATE", entityType: "hero_banner", entityId: id }),
        update: (id: string) =>
            createAuditLog({ action: "UPDATE", entityType: "hero_banner", entityId: id }),
        delete: (id: string) =>
            createAuditLog({ action: "DELETE", entityType: "hero_banner", entityId: id }),
        reorder: () =>
            createAuditLog({ action: "REORDER", entityType: "hero_banner" }),
    },
    admin: {
        create: (email: string) =>
            createAuditLog({ action: "CREATE", entityType: "admin_user", details: { email } }),
    },
};
